<template>
    <vue-custom-scrollbar
        tagname="div"
        :settings="scrollSettings"
        :style="{ height: overflowScroll ? '100%' : 'auto' }"
    >
        <accordion v-if="isVisibleToolbar" :data="accordionData">
            <template v-slot="{ data }">
                <ul class="toolbar-tree" :class="getThemeClass(data)">
                    <toolbar-insert-item
                        v-for="(item, i) in data.items"
                        v-show="isVisibleItem(item)"
                        :key="i"
                        :item="item"
                        :toolbar="data.toolbar"
                        :section="data.section"
                        :category="item.displayName"
                    />
                </ul>
            </template>
        </accordion>

        <div v-else class="toolbar-editor__empty">
            Please select a component.
        </div>
    </vue-custom-scrollbar>
</template>

<script>
import { mapState } from 'vuex';
import vueCustomScrollbar from 'vue-custom-scrollbar';
import 'vue-custom-scrollbar/dist/vueScrollbar.css';

import Accordion from '@/components/utilities/accordion';
import ToolbarInsertItem from '@/components/shared/toolbars/toolbar-insert-item';
import { AccordionItem } from '@/data/data-models';
import { SCREEN_NAMES } from '@/data/constants/screen';
import mapFilters from '@/utils/map-filters';
import { COMPONENT_TREE_SECTIONS } from '@/data/constants/component-tree-sections';

export default {
    name: 'toolbar-insert',
    components: { Accordion, ToolbarInsertItem, vueCustomScrollbar },

    props: {
        screen: {
            type: String,
            required: true,
        },

        toolbar: {
            type: String,
            required: true,
        },

        overflowScroll: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            scrollSettings: {
                suppressScrollX: true,
            },
        };
    },

    computed: {
        ...mapState({
            componentTree: state => state.page.componentTree,
            selectedComponent: state => state.page.selectedComponent,
        }),

        isComponentEditor() {
            return this.screen === SCREEN_NAMES.COMPONENT_EDITOR;
        },

        isComponentToolbar() {
            return this.toolbar === 'component';
        },

        isEditorToolbar() {
            return this.toolbar === 'editor';
        },

        isDynamicMappingToolbar() {
            return this.toolbar === 'dynamic-mapping';
        },

        isTemplateMode() {
            return this.$route.name === 'templates';
        },

        isVisibleToolbar() {
            return this.toolbar !== 'editor' || !this.isComponentEditor || this.selectedComponent;
        },

        accordionData() {
            let componentTreeNew = [];

            switch (true) {
                // for the CE component toolbar (left side), show everything except structural components and atoms
                case this.isComponentEditor && this.isComponentToolbar:
                    componentTreeNew = this.componentTree.filter((section) => {
                        return section.type !== 'elements' && section.name !== COMPONENT_TREE_SECTIONS.STRUCTURAL;
                    });
                    break;

                // for the CE editor toolbar (right side), show everything except structural components
                case this.isComponentEditor && this.isEditorToolbar:
                    componentTreeNew = this.componentTree.filter((section) => {
                        return section.name !== COMPONENT_TREE_SECTIONS.STRUCTURAL;
                    });
                    break;

                // dynamic data mapping modal => component toolbar = only show client components and global content
                case this.isComponentEditor && this.isDynamicMappingToolbar: {
                    let filteredTree = [];

                    filteredTree = this.componentTree.filter((section) => {
                        return section.name === COMPONENT_TREE_SECTIONS.CLIENT || section.name === COMPONENT_TREE_SECTIONS.SHARED;
                    });

                    // we'll also need to filter out any form elements as their data cannot be dynamically mapped
                    filteredTree.forEach((section) => {
                        section.items = section.items.filter((folder) => {
                            folder.children = folder.children.filter((item) => {
                                const hasChildren = !!folder.children.length;

                                if (hasChildren) {
                                    item.children = item.children.filter(nestedItem => !this.isFormMenuItem(nestedItem));

                                    return true;
                                }

                                return !this.isFormMenuItem(item);
                            });

                            return true;
                        });
                    });

                    componentTreeNew = filteredTree;

                    break;
                }

                // page editor (local and connected mode) = show standard components
                case !this.isComponentEditor && process.env.NODE_ENV !== 'production':
                    componentTreeNew = this.componentTree;
                    break;

                // page editor (production mode) = hide standard components
                default:
                    componentTreeNew = this.componentTree.filter(section => !section.pageDisabled);
            }

            return componentTreeNew.map((data) => {
                let dataItems = data.items;

                if (!this.isComponentEditor && data.name === COMPONENT_TREE_SECTIONS.ATOMS) {
                    // we don't want to show the background image atom in the page or template editor
                    // ... it's not meant to be applied to the page level, and only causes UI issues
                    dataItems = dataItems.filter((item) => {
                        return item.name !== 'a-image' || !item.props.isBackgroundImage;
                    });
                }

                return new AccordionItem({
                    id: this.paramCase(data.name),
                    name: data.name,
                    icon: data.icon,
                    iconSet: 'fal',
                    data: {
                        section: data.name,
                        items: dataItems,
                        toolbar: this.toolbar,
                        editable: data.name === COMPONENT_TREE_SECTIONS.CLIENT || data.name === COMPONENT_TREE_SECTIONS.SHARED,
                    },
                });
            });
        },
    },

    methods: {
        ...mapFilters(['paramCase']),

        getThemeClass(data) {
            if (
                data.section === COMPONENT_TREE_SECTIONS.CLIENT
                || data.section === COMPONENT_TREE_SECTIONS.SHARED
                || data.toolbar === 'editor'
            ) {
                return 'toolbar-tree--draggable';
            }

            return '';
        },

        isFormMenuItem(menuItem) {
            const formComponentName = 'o-form-container';
            const menuItemName = menuItem.name.slice(0, menuItem.name.lastIndexOf('_'));

            return menuItemName === formComponentName;
        },

        isVisibleItem(model) {
            return !model.templateOnly || (model.templateOnly && this.isTemplateMode);
        },
    },
};
</script>

<style lang="scss">
.toolbar-tree {
    $this: &;
    $tree-indentation: 23px;
    $button-padding-x: 15px;

    margin: 0;

    &__button {
        display: flex;
        align-items: center;
        border: none;
        padding: 6px $button-padding-x;
        text-align: left;
        font-weight: inherit;
        font-size: inherit;
        width: 100%;

        #{ $this }--draggable #{ $this }__item.#{ $is-hovered } & {
            background-color: var(--accordion-barHoverBackgroundColor1);
        }

        #{ $this }__item--level-3 & {
            padding-left: $button-padding-x + $tree-indentation;
        }

        #{ $this }__item--level-4 & {
            padding-left: $button-padding-x + ($tree-indentation * 2);
        }
    }

    &__button-icon {
        color: var(--accordion-barColor1);
        margin-right: 10px;
    }

    &__button-text {
        pointer-events: none;
    }

    &__button-arrow {
        margin-left: auto;
    }

    &__item {
        list-style: none;
        font-size: 13px;
        text-align: left;
        font-weight: 600;

        &--level-1 {
            color: var(--accordion-barColor1);
            background-color: var(--accordion-barBackgroundColor1);

            &:hover {
                color: var(--accordion-barHoverColor1);
                background-color: var(--accordion-barHoverBackgroundColor1);
            }

            &:not(:first-of-type) {
                border-top: 1px solid var(--accordion-borderColor1);
            }
        }

        &--level-2 {
            color: var(--accordion-barColor2);
            background-color: var(--accordion-barBackgroundColor2);
            font-weight: 600;

            &:hover {
                color: var(--accordion-barHoverColor2);
                background-color: var(--accordion-barHoverBackgroundColor2);
            }

            &:not(:first-of-type) {
                border-top: 1px solid var(--accordion-borderColor2);
            }
        }

        &--level-3 {
            color: var(--accordion-barColor3);
            background-color: var(--accordion-barBackgroundColor3);
            font-weight: 600;

            &:hover {
                color: var(--accordion-barHoverColor3);
                background-color: var(--accordion-barHoverBackgroundColor3);
            }

            &:not(:first-of-type) {
                border-top: 1px solid var(--accordion-borderColor3);
            }
        }

        &--is-item {
            color: var(--app-linkColor);

            &:hover {
                color: var(--app-linkHoverColor);
            }
        }
    }

    &__component {
        display: flex;
        align-items: center;
        padding: 6px $button-padding-x;

        &:hover {
            cursor: pointer;
        }

        &--drag {
            &:hover {
                cursor: grab;
            }
        }

        &--editable {
            // top/bottom padding (spacing) is provided by the action icons
            padding: 0 1.5rem 0 0;
            margin-top: 4px;
            margin-bottom: 4px;
        }

        #{ $this }__item--level-3 & {
            padding-left: $button-padding-x + $tree-indentation;
        }

        #{ $this }__item--level-4 & {
            padding-left: $button-padding-x + ($tree-indentation * 2);
        }
    }

    &__grip {
        margin-right: 10px;
        font-size: 16px;
        visibility: hidden;

        &:hover {
            cursor: grab;
        }

        #{ $this }__component:hover & {
            visibility: visible;
        }
    }

    &__text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        border: 1px solid transparent;
    }

    &__edit {
        color: var(--form-inputColor);
        background-color: var(--form-inputBackgroundColor);
        border: 1px solid var(--form-inputBorderColor);
        padding: 0 5px;
        max-width: 140px;
        font-size: 0.875rem;
    }

    &-folder {
        margin:6px 16px;

        #{ $this }__item--level-3 & {
            margin-left: $button-padding-x + $tree-indentation;
        }

        #{ $this }__item--level-4 & {
            margin-left: $button-padding-x + ($tree-indentation * 2);
        }
    }

    &__actions {
        display: flex;
        margin-left: auto;
        position: relative;
        right: -20px;
    }

    &__action {
        padding: 5px 11px;
        transition: transform 0.15s;

        &:hover {
            transform: scale(1.2);
            cursor: pointer;
        }

        &--edit {
            &:hover {
                color: var(--app-linkHoverColor);
            }
        }

        &--copy {
            &:hover {
                color: var(--app-linkHoverColor);
            }
        }

        &--delete {
            &:hover {
                color: var(--app-linkHoverColor);
            }
        }
    }

    &__empty {
        color: var(--app-color);
        opacity: 0.6;
        padding: 6px 30px;
    }
}
</style>
